/**
 * The anonymous login calls. They go through fetch directly instead of
 * the shared client because the client's ApiError has no field for the
 * machine-readable `code` and `retryAfterMs` a refused code request carries.
 *
 * The base URL is injected: the web reads it from its build configuration,
 * the native app from its app config. Neither may ever be reached from the
 * demo (demoNoNetwork.test.ts forbids this file in the demo's import graph).
 */
import { de } from '@fsv/core';
import type { Me } from './types';

export class LoginError extends Error {
  constructor(
    public readonly status: number,
    message: string,
    public readonly code?: string,
    public readonly retryAfterMs?: number,
  ) {
    super(message);
    this.name = 'LoginError';
  }
}

interface Body {
  error?: string;
  code?: string;
  retryAfterMs?: number;
}

export interface LoginApi {
  requestCode(email: string): Promise<unknown>;
  verifyCode(email: string, code: string): Promise<{ token: string; me: Me }>;
}

export interface LoginApiOptions {
  /** Absolute API base including /api. A function so a configuration read late is still honoured. */
  baseUrl: string | (() => string);
  /** Defaults to the global fetch, looked up per call so a test stub sees every request. */
  fetchImpl?: typeof fetch;
}

export function createLoginApi(options: LoginApiOptions): LoginApi {
  const base = () => (typeof options.baseUrl === 'function' ? options.baseUrl() : options.baseUrl);
  const doFetch: typeof fetch = options.fetchImpl ?? ((input, init) => fetch(input, init));

  async function post<T>(path: string, body: unknown): Promise<T> {
    let res: Response;
    try {
      res = await doFetch(`${base()}${path}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify(body),
      });
    } catch {
      throw new LoginError(0, de.errors.offline);
    }
    const data = (await res.json().catch(() => ({}))) as Body & T;
    if (!res.ok) {
      const retry = data.retryAfterMs ?? (Number(res.headers.get('Retry-After')) * 1000 || undefined);
      throw new LoginError(res.status, data.error ?? de.errors.generic, data.code, retry);
    }
    return data;
  }

  return {
    requestCode: (email) => post('/auth/code', { email }),
    verifyCode: (email, code) => post('/auth/verify', { email, code }),
  };
}
